import axios from './index';

/**
 * APIs
 */
// table list
export type UserListResponse = {
    code: number;
    data: User[];
    meta: {
        cursor?: string;
        more: boolean;
        page?: number | null;
        size: number;
        total: number;
    };
    msg: string;
};

export const userListApi = (query: Record<string, any>) => {
    const { search_after, size, ...rest } = query || {};

    const params: Record<string, any> = {
        ...rest,
    };

    if (search_after) {
        params.cursor = search_after;
    }

    if (size) {
        params.size = size;
    }

    return axios<UserListResponse>('/v1/users', {
        method: 'get',
        params,
        paramsSerializer: {
            indexes: true,
        },
    });
};

export const userDetailApi = (uid: string) => {
    return axios<ResType<User>>(`/v2/users/${uid}`, {
        method: 'get',
    });
};

/** 导出用户：参数与列表查询一致 */
export const userExport = (params?: UserListParams) => {
    return axios<ResType<unknown>>('/v1/users/export', {
        method: 'get',
        params,
        paramsSerializer: {
            indexes: true,
        },
    });
};

// 获取用户限制状态
export const userTnsApi = (user_id: string) => {
    return axios<ResType<UserTns>>(`/v1/tns/users/${user_id}`, {
        method: 'get',
    });
};

// 获取用户操作记录
export const userTnsLogs = (params: UserTnsLogsReq) => {
    return axios<PageResType<UserTnsLogs>>('/v1/tns/users/logs', {
        method: 'get',
        params,
    });
};

// 禁言接口
export const userMute = (user_id: string, data: UserTnsAction) => {
    return axios(`/v1/tns/users/${user_id}/mute`, {
        method: 'put',
        data,
    });
};

// 封号接口
export const userBan = (user_id: string, data: UserTnsAction) => {
    return axios(`/v1/tns/users/${user_id}/ban`, {
        method: 'put',
        data,
    });
};

// 解除禁言接口
export const userUnmute = (user_id: string, data: UserTnsAction) => {
    return axios(`/v1/tns/users/${user_id}/unmute`, {
        method: 'put',
        data,
    });
};

// 解除封号接口
export const userUnban = (user_id: string, data: UserTnsAction) => {
    return axios(`/v1/tns/users/${user_id}/unban`, {
        method: 'put',
        data,
    });
};

// 资金冻结
export const userFreezeFounds = (user_id: string, data: UserTnsAction) => {
    return axios(`/v1/tns/users/funds/${user_id}/freeze`, {
        method: 'put',
        data,
    });
};

// 解冻资金
export const userUnfreezeFounds = (user_id: string, data: UserTnsAction) => {
    return axios(`/v1/tns/users/funds/${user_id}/unfreeze`, {
        method: 'put',
        data,
    });
};

//
// 资金冻结
export const userDisableLive = (user_id: string, data: UserTnsAction) => {
    return axios(`/v1/tns/users/${user_id}/disable_live`, {
        method: 'put',
        data,
    });
};

// 解冻资金
export const userEnableLive = (user_id: string, data: UserTnsAction) => {
    return axios(`/v1/tns/users/${user_id}/enable_live`, {
        method: 'put',
        data,
    });
};

// 重置用户个人资料
export const userProfileReset = (user_id: string, data: UserProfileResetParams) => {
    return axios(`/v1/tns/users/${user_id}/reset`, {
        method: 'put',
        data,
    });
};

// 获取用户身份验证历史
export const userIdentityVerificationHistory = (params: UserIdentityVerificationHistoryParams) => {
    return axios<PageResType<UserIdentityVerificationHistory>>('/v1/users/verification', {
        method: 'get',
        params,
    });
};

// 导出用户身份验证历史
export const userIdentityVerificationHistoryExport = (params: UserIdentityVerificationHistoryParams) => {
    return axios('/v1/users/verification/export', {
        method: 'get',
        params,
    });
};

// 公共服务 获取用户
export const gUser = (params: UserParams) => {
    return axios<PageResDataType<UserResult>>('/ps/v1/users', {
        method: 'get',
        params,
    });
};

// 添加名人
export const addCelebrity = (data: any) => {
    return axios<PageResType<UserResult>>('/v1/users/celebrity', {
        method: 'post',
        data,
    });
};

// 删除名人
export const dCelebrity = (user_id: string) => {
    return axios<PageResType<UserResult>>(`/v1/users/celebrity/${user_id}`, {
        method: 'delete',
    });
};

// 导出名人
export const exportCelebrity = (data: any) => {
    return axios<PageResType<UserResult>>('/v1/users/celebrity/export', {
        method: 'get',
        data,
    });
};

/**
 * Types
 */
import type { PageResDataType, PageResType, ResType } from '@/typings/common';

export type UserInfoAccount = {
    area_code: string;
    city: string;
    country: string;
    country_code: string;
    email: string;
    invitation_code: string;
    mobile: string;
    register_by: string;
};

export type UserPost = {
    debate_count: string;
    normal_count: string;
    total_count: string;
};

export type UserRelation = {
    block_count: string;
    following_count: string;
    follower_count: string;
    friend_count: string;
    inviter_id: string;
};

export type UpdateUserTrustPayload = {
    trust: number;
    reason: string;
};

export const updateUserTrust = (userId: string, data: UpdateUserTrustPayload) =>
    axios<ResType<null>>(`/v1/users/${userId}/trust`, {
        method: 'post',
        data,
    });

// user item
export type User = {
    id: string;
    trust?: string | number;
    avatar: string;
    avatar_is_nft: boolean;
    bio: string;
    gender: string;
    language: string;
    nickname: string;
    username: string;
    wallet_address?: string | null;
    name?: string;
    created_at: string;
    updated_at: string;
    is_banned: boolean;
    is_muted: boolean;
    time_unban: number;
    time_unmute: number;
    info_account: UserInfoAccount;
    stat_post: UserPost;
    stat_relation: UserRelation;
    deleted_at: string | null;
    is_funds_frozen: boolean;
    is_celebrity: boolean;
    is_live_baned: boolean;
    time_live_unban: number;
};

/** /v1/users 列表查询参数 */
export type UserListParams = {
    /** 用户 UID */
    id?: string;
    username?: string;
    nickname?: string;
    /** 邀请人 UID */
    invite_uid?: string;
    /** 注册时间：[开始时间, 结束时间] */
    created_at?: [string, string];
};

/** @deprecated 使用 UserListParams；保留兼容旧调用 */
export type UserDatasExportParams = UserListParams;

export type UserQuery = {
    uid?: string;
    name?: string;
};

/**
 *
 */
export type UserTnsOperator = {
    name: string;
    id: string;
    created_at: string;
    deleted_at: string;
    updated_at: string;
};

/**
 * 用户封禁信息
 */
export type UserTns = {
    id: string;
    created_at?: string;
    updated_at?: string;
    user_id: string;
    mute_status: boolean;
    unmute_at?: string;
    ban_status: boolean;
    unban_at?: string;
};

/**
 * 用户封禁记录
 */
export type UserTnsLogs = {
    id: string;
    user_id: string;
    type: UserAction;
    period: number;
    period_unit: 'minute' | 'hour' | 'day';
    remark: string;
    operator_account?: UserTnsOperator;
    created_at: string;
    updated_at: string;
    userInfo: User;
    content: string[];
};

/**
 * 用户封禁记录查询条件
 */
export type UserTnsLogsReq = {
    page?: number;
    limit?: number;
    order_by?: string;
    order_sort?: string;
    user_id?: string;
    type?: UserAction;
    types?: UserAction[];
    operator_account_id?: string;
    operation_start?: string;
    operation_end?: string;
};

/**
 * 禁言/封号接口
 */
export type UserTnsAction = {
    period?: number;
    period_unit?: 'minute' | 'hour' | 'day';
    remark?: string;
    reason?: string;
};

// user action
export type UserAction =
    | 'temp_mute'
    | 'perma_mute'
    | 'temp_ban'
    | 'perma_ban'
    | 'unmute'
    | 'unban'
    | 'profile'
    | 'funds_freeze'
    | 'funds_unfreeze'
    | 'temp_disable_live'
    | 'perma_disable_live';

export type UserIdentityVerificationStatus = 'Verified' | 'Unverified';

export type UserIdentityVerificationHistoryParams = {
    page?: number;
    limit?: number;
    uid?: string;
    username?: string;
    nickname?: string;
    identity_status?: UserIdentityVerificationStatus;
    liveness2_status?: UserIdentityVerificationStatus;
    liveness_start?: string;
    liveness_end?: string;
    identity_start?: string;
    identity_end?: string;
    liveness2_start?: string;
    liveness2_end?: string;
};

export type UserIdentityVerificationHistory = {
    uid: string;
    user_info: {
        id: string;
        username: string;
        nickname: string;
    };
    type: string;
    status: string;
    identity_status: UserIdentityVerificationStatus;
    identity_at: string;
    liveness_at: string;
    liveness2_status: UserIdentityVerificationStatus;
    liveness2_at: string;
};

export enum UserActionType {
    TEMP_MUTE = 'temp_mute',
    PERMA_MUTE = 'perma_mute',
    TEMP_BAN = 'temp_ban',
    PERMA_BAN = 'perma_ban',
    UNMUTE = 'unmute',
    UNBAN = 'unban',
}

export enum UserActionName {
    TEMP_MUTE = '临时禁言',
    PERMA_MUTE = '永久禁言',
    TEMP_BAN = '临时封号',
    PERMA_BAN = '永久封号',
    UNMUTE = '解除禁言',
    UNBAN = '解除封号',
}

export const userActionOptions = [
    {
        label: UserActionName.TEMP_MUTE,
        value: UserActionType.TEMP_MUTE,
    },
    {
        label: UserActionName.PERMA_MUTE,
        value: UserActionType.PERMA_MUTE,
    },
    {
        label: UserActionName.TEMP_BAN,
        value: UserActionType.TEMP_BAN,
    },
    {
        label: UserActionName.PERMA_BAN,
        value: UserActionType.PERMA_BAN,
    },
    {
        label: UserActionName.UNMUTE,
        value: UserActionType.UNMUTE,
    },
    {
        label: UserActionName.UNBAN,
        value: UserActionType.UNBAN,
    },
];

export type UserProfileResetParams = {
    items: UserPrefileResetItem[];
    remark?: string;
};

export enum UserPrefileResetItem {
    Avatar = 'avatar',
    Bio = 'bio',
    Name = 'name',
    Nickname = 'nickname',
}

export type UserParams = {
    /**
     * 创建时间结束（注册时间）
     */
    created_end?: string;
    /**
     * 创建时间开始（注册时间）
     */
    created_start?: string;
    /**
     * 用户 ID
     */
    id?: string;
    /**
     * 邀请码
     */
    invitation_code?: string;
    /**
     * 邀请人 ID
     */
    inviter_id?: string;
    /**
     * 是否为名人
     */
    is_celebrity?: string;
    /**
     * 昵称（精确匹配）
     */
    nickname?: string;
    /**
     * 游标
     */
    search_after?: string;
    /**
     * 每页条数
     */
    size?: string;
    /**
     * 排序
     */
    'sort[0][created_at]'?: string;
    /**
     * 排序
     */
    'sort[1][id]'?: string;
    /**
     * 用户名（精确匹配）
     */
    username?: string;

    'sort[0][updated_at]'?: string;
};

export type UserResult = {
    avatar: string;
    avatar_is_nft: boolean;
    bio: string;
    created_at: string;
    deleted_at?: null;
    gender: string;
    id: string;
    info_account: InfoAccount;
    is_banned: boolean;
    is_muted: boolean;
    language: string;
    nickname: string;
    name: string;
    stat_post: StatPost;
    stat_relation: StatRelation;
    time_unban: number;
    time_unmute: number;
    updated_at: string;
    username: string;
    user_id: string;
};

export type InfoAccount = {
    area_code: string;
    city: string;
    country: string;
    country_code: string;
    email: string;
    invitation_code: string;
    mobile: string;
    register_by: string;
};

export type StatPost = {
    debate_count: string;
    normal_count: string;
    total_count: string;
};

export type StatRelation = {
    block_count: string;
    follower_count: string;
    following_count: string;
    friend_count: string;
    inviter_id: string;
};
